import { z } from 'zod/v4';
import {
  IOnDemandBookableServiceOption,
  IOnDemandAvailabilityRequest,
  IOnDemandConnectionOption,
  IOnDemandAvailabilityResponse,
  IOnDemandBookingRequest,
  IOnDemandBookingConfirmationRequest,
  IOnDemandBookingResponse,
  IOnDemandCancellationRequest,
  IOnDemandCancellationResponse,
  IOnDemandLocation,
} from './on-demand-booking.interfaces';
import { OPERATOR_MAPPING_ID_SCHEMA } from '../cms/operator-mapping.interface';
import { TRIAS_BOOKING_PT_LEG_SCHEMA } from '../journey-planning/trias.validations';

// ─── Bookable Service Option ─────────────────────────────────────────────────

export const ON_DEMAND_BOOKABLE_SERVICE_OPTION_SCHEMA: z.ZodType<IOnDemandBookableServiceOption> =
  z.object({
    label: z.string(),
    mobilityUserField: z.string(),
    maxCapacity: z.number(),
  });

// ─── Availability ───────────────────────────────────────────────────────────

export const ON_DEMAND_LOCATION_SCHEMA: z.ZodType<IOnDemandLocation> =
  z.intersection(
    z.union([
      z.object({
        latitude: z.number(),
        longitude: z.number(),
      }),
      z.object({
        stopPointRef: z.string(),
      }),
    ]),
    z.object({
      name: z.string(),
    })
  );

export const ON_DEMAND_AVAILABILITY_REQUEST_SCHEMA: z.ZodType<IOnDemandAvailabilityRequest> =
  z.object({
    operatorMappingId: OPERATOR_MAPPING_ID_SCHEMA,
    pickUp: ON_DEMAND_LOCATION_SCHEMA,
    setDown: ON_DEMAND_LOCATION_SCHEMA,
    earliestDeparture: z.iso.datetime({ offset: true }).optional(),
    latestArrival: z.iso.datetime({ offset: true }).optional(),
    passengerCount: z.number(),
    extrasPerPassenger: z.array(z.array(z.string())).optional(),
  });

export const ON_DEMAND_CONNECTION_OPTION_SCHEMA: z.ZodType<IOnDemandConnectionOption> =
  z.object({
    connectionInformation: TRIAS_BOOKING_PT_LEG_SCHEMA,
    departureTimetabled: z.iso.datetime({ offset: true }).optional(),
    arrivalTimetabled: z.iso.datetime({ offset: true }).optional(),
    lineName: z.string().optional(),
    bookableServiceOptions: z.array(ON_DEMAND_BOOKABLE_SERVICE_OPTION_SCHEMA),
    price: z.number().optional(),
  });

export const ON_DEMAND_AVAILABILITY_RESPONSE_SCHEMA: z.ZodType<IOnDemandAvailabilityResponse> =
  z.object({
    isAvailable: z.boolean(),
    connectionOptions: z.array(ON_DEMAND_CONNECTION_OPTION_SCHEMA),
    rejectionReason: z.string().optional(),
  });

// ─── Booking / Reservation ──────────────────────────────────────────────────

export const ON_DEMAND_BOOKING_REQUEST_SCHEMA: z.ZodType<IOnDemandBookingRequest> =
  z.object({
    operatorMappingId: OPERATOR_MAPPING_ID_SCHEMA,
    appCode: z.string().min(1),
    connectionInformation: TRIAS_BOOKING_PT_LEG_SCHEMA,
    earliestDeparture: z.iso.datetime({ offset: true }),
    latestArrival: z.iso.datetime({ offset: true }).optional(),
    passengerCount: z.number(),
    isReservation: z.boolean(),
    extrasPerPassenger: z.array(z.array(z.string())).optional(),
    systemRef: z.string().optional(),
    bookingCode: z.string().optional(),
    customerName: z.string(),
    customerEmail: z.string(),
    customerPhone: z.string().optional(),
  });

export const ON_DEMAND_BOOKING_CONFIRMATION_REQUEST_SCHEMA: z.ZodType<IOnDemandBookingConfirmationRequest> =
  z.object({
    operatorMappingId: OPERATOR_MAPPING_ID_SCHEMA,
    appCode: z.string().min(1),
    systemRef: z.string().min(1),
    bookingCode: z.string().min(1),
    customerName: z.string(),
    customerEmail: z.string(),
    customerPhone: z.string().optional(),
  });

export const ON_DEMAND_BOOKING_RESPONSE_SCHEMA: z.ZodType<IOnDemandBookingResponse> =
  z.object({
    bookingRef: z.string(),
    systemRef: z.string(),
    reservedUntil: z.string().optional(),
    changeableUntil: z.string().optional(),
    cancellableUntil: z.string().optional(),
    confirmedDeparture: z.string().optional(),
    confirmedArrival: z.string().optional(),
  });

// ─── Cancellation ───────────────────────────────────────────────────────────

export const ON_DEMAND_CANCELLATION_REQUEST_SCHEMA: z.ZodType<IOnDemandCancellationRequest> =
  z.object({
    operatorMappingId: OPERATOR_MAPPING_ID_SCHEMA,
    appCode: z.string().min(1),
    bookingRef: z.string().min(1),
    systemRef: z.string().min(1),
    customerName: z.string().min(1).optional(),
    customerEmail: z.email().optional(),
  });

export const ON_DEMAND_CANCELLATION_RESPONSE_SCHEMA: z.ZodType<IOnDemandCancellationResponse> =
  z.object({
    errorMessage: z.string().optional(),
  });
